/**
 * Generic DataTable component for displaying tabular data.
 * Usage: Provide columns and data props. Supports custom renderers and sorting.
 */
import React from "react";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import useCollectSort from "hooks/useCollectSort";

export type DataTableColumn<T> = {
    label: React.ReactNode;
    key: string;
    render?: (row: T) => React.ReactNode;
    colProps?: any;
    sortable?: boolean;
    sortKey?: string; // Optional key to use for sorting (different from display key)
};

export interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    data: T[];
    rowKey?: (row: T, idx: number) => React.Key;
    emptyContent?: React.ReactNode;
    className?: string;
    onRowClick?: (row: T, tabType?: string) => void;
    /** When provided, the first-column name link uses this URL so "open in new tab" works correctly. */
    getRowTo?: (row: T, tabType?: string) => string;
    tabType?: string;
    hideHeadersWhenEmpty?: boolean;
}

function DataTable<T extends object>({
    columns,
    data,
    rowKey,
    emptyContent,
    className = "cstm-table mt-4",
    onRowClick,
    getRowTo,
    tabType,
    hideHeadersWhenEmpty = false,
}: Readonly<DataTableProps<T>>) {
    const { data: sortedData, sortIcon } = useCollectSort(data, undefined, {
        resetOnCollectionChange: false,
    });

    // Helper to get the sort key for a column (uses sortKey if provided, otherwise uses key)
    const getSortKey = (col: DataTableColumn<T>) => col.sortKey ?? col.key;

    // Determine if headers should be shown
    // If hideHeadersWhenEmpty is true, only show headers when there is data
    // Otherwise, always show headers (default behavior)
    const hasData = Array.isArray(data) && data.length > 0;
    const shouldShowHeaders = hideHeadersWhenEmpty ? hasData : true;
    return (
        <Row className={className}>
            <Col sm={12}>
                {/* Show table header based on hideHeadersWhenEmpty prop */}
                {shouldShowHeaders && (
                    <div className="table-head-fixed">
                        <Row className="no-checkbox">
                            {columns.map((col, idx) => {
                                return (
                                    <Col
                                        key={col.key}
                                        {...(col.colProps || {})}
                                    >
                                        {col.label}{" "}
                                        {sortedData.length > 0 && col.sortable !== false
                                            ? sortIcon(getSortKey(col))
                                            : null}
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                )}
                <div>
                    {sortedData.length === 0 && emptyContent && (
                        <div className="table-row bg-transparent">
                            <Row className="no-checkbox">
                                <Col sm={12} className="text-center">
                                    {emptyContent}
                                </Col>
                            </Row>
                        </div>
                    )}
                    {sortedData.map((row, idx) => (
                        <div
                            className="table-row"
                            key={rowKey ? rowKey(row, idx) : idx}
                        >
                            <Row className="no-checkbox">
                                {columns.map((col, cidx) => {
                                    const isFirstColumn = cidx === 0;
                                    const hasNameKey =
                                        col.key
                                            .toLowerCase()
                                            .includes("name") ||
                                        col.key.toLowerCase().includes("id");

                                    let cellContent: React.ReactNode;
                                    if (col.render) {
                                        cellContent = col.render(row);
                                    } else if (isFirstColumn && onRowClick && hasNameKey) {
                                        const to = getRowTo?.(row, tabType);
                                        const useRealHref = to && to !== "#";
                                        cellContent = (
                                            <Link
                                                to={to ?? "#"}
                                                onClick={useRealHref ? undefined : (e) => {
                                                    e.preventDefault();
                                                    onRowClick(
                                                        row,
                                                        tabType
                                                    );
                                                }}
                                            >
                                                {(row as any)[col.key] ?? "-"}
                                            </Link>
                                        );
                                    } else {
                                        cellContent = (row as any)[col.key] ?? "-";
                                    }

                                    return (
                                        <Col
                                            key={col.key + cidx}
                                            {...(col.colProps || {})}
                                        >
                                            {cellContent}
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    ))}
                </div>
            </Col>
        </Row>
    );
}

export default DataTable;
